export const DEFAULT_CATEGORY_NAME = 'Education';

export const slugify = (text) =>
  text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');

const apiFetch = async (url, options = {}) => {
  const res = await fetch(url, { credentials: 'include', ...options });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
};

export const db = {
  init: () => {}, // no-op — D1 is already initialised server-side

  // ---- Articles ----
  getArticles: async (publishedOnly = true) => {
    const data = await apiFetch(`/api/articles?published=${publishedOnly}`);
    return data.articles || [];
  },

  getArticlesWithCategory: async (publishedOnly = true) => {
    return db.getArticles(publishedOnly);
  },

  getArticleBySlug: async (slug) => {
    const data = await apiFetch(`/api/articles?slug=${encodeURIComponent(slug)}`);
    return data.article || null;
  },

  getArticleById: async (id) => {
    const data = await apiFetch(`/api/articles?id=${encodeURIComponent(id)}`);
    return data.article || null;
  },

  getArticleCategory: (article) => {
    return article?.category || null;
  },

  saveArticle: async (article) => {
    if (article.id && !article._isNew) {
      await apiFetch(`/api/articles?id=${encodeURIComponent(article.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(article)
      });
    } else {
      const result = await apiFetch('/api/articles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(article)
      });
      return { ...article, id: result.id };
    }
    return article;
  },

  deleteArticle: async (id) => {
    await apiFetch(`/api/articles?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  // ---- Categories ----
  getCategories: async () => {
    const data = await apiFetch('/api/categories');
    return data.categories || [];
  },

  getCategoryById: async (id) => {
    const cats = await db.getCategories();
    return cats.find(c => c.id === id) || null;
  },

  getCategoryBySlug: async (slug) => {
    const cats = await db.getCategories();
    return cats.find(c => c.slug === slug) || null;
  },

  getCategoryByName: async (name) => {
    const cats = await db.getCategories();
    return cats.find(c => c.name.toLowerCase() === name.toLowerCase()) || null;
  },

  saveCategory: async (category) => {
    if (category.id) {
      await apiFetch(`/api/categories?id=${encodeURIComponent(category.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(category)
      });
    } else {
      await apiFetch('/api/categories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(category)
      });
    }
  },

  deleteCategory: async (id) => {
    await apiFetch(`/api/categories?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  // ---- Links ----
  getLinks: async () => {
    const data = await apiFetch('/api/links');
    return data.links || [];
  },

  saveLink: async (link) => {
    if (link.id) {
      await apiFetch(`/api/links?id=${encodeURIComponent(link.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(link)
      });
    } else {
      const result = await apiFetch('/api/links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(link)
      });
      return { ...link, id: result.id };
    }
    return link;
  },

  deleteLink: async (id) => {
    await apiFetch(`/api/links?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  // ---- Media ----
  getMedia: async () => {
    const data = await apiFetch('/api/media');
    return data.media || [];
  },

  uploadMedia: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const result = await apiFetch('/api/media', {
      method: 'POST',
      body: formData
    });
    return result.media;
  },

  deleteMedia: async (id) => {
    await apiFetch(`/api/media?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  // ---- View tracking ----
  recordView: (article_id) => {
    fetch('/api/view', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ article_id }),
    }).catch(() => {}); // fire-and-forget, never blocks the page
  },

  countArticlesInCategory: async (id) => {
    const cats = await db.getCategories();
    const cat = cats.find(c => c.id === id);
    return cat?.article_count || 0;
  }
};